import { useState, useEffect } from 'react'
import { getLocalDateStr } from '../utils/dailyPuzzleGen'
import { posterUrl, logoUrl, getMoviesOnThisDate, getYourNextStream, getWatchProviders } from '../api/tmdb'
import TmdbFooter from './TmdbFooter'
import BrandLogo from './BrandLogo'
import DailyCard from './DailyCard'
import { isNativePlatform } from '../services/revenueCat'
import { IconTicket, IconClapper, IconFlame, IconPlay, IconCalendar, IconReel, IconSwords, IconHandshake, IconTv, IconArrowRight, IconX, IconAppStore, IconApple } from './icons'

const APP_STORE_URL = 'https://apps.apple.com/us/app/action-the-movie-game/id6770584055'

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(ts) {
  if (!ts) return ''
  const diffMs = Date.now() - ts
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHrs = Math.floor(diffMins / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  return `${diffDays}d ago`
}

const TYPE_BADGE = {
  single:     { Icon: IconPlay,      label: 'Single' },
  daily:      { Icon: IconCalendar,  label: 'Daily' },
  historical: { Icon: IconReel,      label: 'Historical' },
  h2h:        { Icon: IconSwords,    label: 'H2H' },
  coop:       { Icon: IconHandshake, label: 'Co-Op' },
}

const STATUS_LABEL = {
  lobby:     'In Lobby',
  picking:   'Picking Actors',
  playing:   'In Progress',
  paused:    'Paused',
  abandoned: 'Abandoned',
  completed: 'Completed',
}

// ── SavedGameCard ─────────────────────────────────────────────────────────────

function SavedGameCard({ entry, onResume, onAbandon }) {
  const [confirmAbandon, setConfirmAbandon] = useState(false)

  const badge = TYPE_BADGE[entry.type] ?? { Icon: IconClapper, label: entry.type }
  const statusLabel = STATUS_LABEL[entry.status] ?? entry.status
  const startName = entry.actors?.start?.name
  const goalName  = entry.actors?.goal?.name
  const pairLine  = startName && goalName ? `${startName} → ${goalName}` : '…'

  return (
    <div className="sgd-card">
      <div className="sgd-card__header">
        <span className="sgd-card__badge">
          <span>{badge.Icon ? <badge.Icon size={13} /> : null}</span>
          <span>{badge.label}</span>
        </span>
        <span className={`sgd-card__status sgd-card__status--${entry.status}`}>
          {statusLabel}
        </span>
      </div>

      <p className="sgd-card__pair">{pairLine}</p>

      <p className="sgd-card__meta">
        {entry.refundEligible
          ? <span className="sgd-card__refund"><IconTicket size={12} style={{ verticalAlign: 'middle' }} /> Refund eligible</span>
          : <span className="sgd-card__no-refund">No refund</span>
        }
        {' · '}
        {relativeTime(entry.createdAt)}
      </p>

      {confirmAbandon ? (
        <div className="sgd-card__confirm">
          <span className="sgd-card__confirm-msg">
            {entry.refundEligible
              ? "Abandon this game? You'll get your ticket back."
              : "No refund available — you've made progress. Abandon anyway?"}
          </span>
          <div className="sgd-card__confirm-btns">
            <button
              type="button"
              className="btn-primary sgd-card__confirm-yes"
              onClick={() => { setConfirmAbandon(false); onAbandon(entry) }}
            >
              Abandon
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setConfirmAbandon(false)}
            >
              Keep It
            </button>
          </div>
        </div>
      ) : (
        <div className="sgd-card__actions">
          <button
            type="button"
            className="btn-primary sgd-card__resume"
            onClick={() => onResume(entry)}
          >
            Resume <IconClapper size={14} style={{ verticalAlign: 'middle' }} />
          </button>
          <button
            type="button"
            className={`btn-ghost sgd-card__abandon${entry.status === 'paused' ? ' sgd-card__abandon--destructive' : ''}`}
            onClick={() => setConfirmAbandon(true)}
          >
            {entry.status === 'paused' ? 'Abandon (no refund)' : 'Abandon'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── HomeScreen ────────────────────────────────────────────────────────────────

const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='94' height='141' viewBox='0 0 94 141'%3E%3Crect fill='%23333' width='94' height='141'/%3E%3C/svg%3E"

function buildStreamingList(providers) {
  if (!providers) return []
  const all = [
    ...(providers.flatrate ?? []),
    ...(providers.rent     ?? []),
    ...(providers.buy      ?? []),
  ]
  const seen = new Set()
  return all
    .filter((p) => { if (seen.has(p.provider_id)) return false; seen.add(p.provider_id); return true })
    .slice(0, 2)
}

function formatFlipDate(dateStr) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function PosterFlipCard({ film }) {
  const [flipped,          setFlipped]          = useState(false)
  const [providers,        setProviders]        = useState(null)
  const [providersFetched, setProvidersFetched] = useState(false)

  // 'tv' results from the Your Next Stream feed carry mediaType: 'tv' so the
  // watch-providers call hits /tv/{id}/... instead of /movie/{id}/...
  const isTV = film.mediaType === 'tv' || film.media_type === 'tv'

  const handleFlip = () => {
    setFlipped((f) => !f)
    if (!providersFetched) {
      setProvidersFetched(true)
      getWatchProviders(film.id, isTV ? 'tv' : 'movie')
        .then((data) => setProviders(data))
        .catch(()    => setProviders({}))
    }
  }

  const src         = film.poster_path ? posterUrl(film.poster_path, 'w185') : MOVIE_PH
  const releaseDate = formatFlipDate(film.release_date)
  const streamList  = buildStreamingList(providers)
  const loading     = flipped && !providersFetched

  // Released in the last 90 days → likely still in theaters. Used to swap the
  // empty-providers fallback from "Not currently streaming" to "In Theaters"
  // so a fresh release doesn't read as unavailable. `film.release_date` is
  // already on the prop (normalized for TV in getYourNextStream).
  const isRecentRelease = !!film.release_date
    && (new Date() - new Date(film.release_date)) < (90 * 24 * 60 * 60 * 1000)

  return (
    <div
      className="poster-flip"
      onClick={handleFlip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFlip()}
      aria-label={`${film.title} — tap to flip`}
    >
      <div className={`poster-flip__inner${flipped ? ' poster-flip__inner--flipped' : ''}`}>
        <div className="poster-flip__front">
          <img src={src} alt={film.title} draggable={false} />
          {isTV && (
            <span className="poster-flip__tv-badge" aria-label="TV series"><IconTv size={11} style={{ verticalAlign: 'middle' }} /> TV</span>
          )}
        </div>
        <div className="poster-flip__back" aria-hidden={!flipped}>
          <p className="poster-flip__title">{film.title}</p>
          {releaseDate && <p className="poster-flip__date">{releaseDate}</p>}
          <div className="poster-flip__streaming">
            {loading ? (
              <span className="poster-flip__loading">…</span>
            ) : streamList.length > 0 ? (
              streamList.map((p) => (
                <div key={p.provider_id} className="poster-flip__provider">
                  {p.logo_path && (
                    <img
                      className="poster-flip__provider-logo"
                      src={logoUrl(p.logo_path)}
                      alt={p.provider_name}
                    />
                  )}
                  <span className="poster-flip__provider-name">{p.provider_name}</span>
                </div>
              ))
            ) : isRecentRelease ? (
              <span className="poster-flip__in-theaters">
                <IconTicket size={12} aria-hidden="true" />
                In Theaters
              </span>
            ) : (
              <span className="poster-flip__no-stream">Not currently streaming</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function OnThisDateSection({ films }) {
  if (!films || films.length === 0) return null
  return (
    <div className="on-this-date">
      <div className="on-this-date__heading">
        <span className="on-this-date__label">Premiered Today</span>
        <span className="on-this-date__hint">Tap for details</span>
      </div>
      <div className="on-this-date__card">
        {films.map((film) => <PosterFlipCard key={film.id} film={film} />)}
      </div>
    </div>
  )
}

function TrendingSection({ films }) {
  if (!films || films.length === 0) return null
  return (
    <div className="trending">
      <div className="trending__heading">
        <span className="trending__label">Trending This Week</span>
        <span className="trending__hint">Tap for details</span>
      </div>
      <div className="trending__card">
        {films.map((film) => <PosterFlipCard key={film.id} film={film} />)}
      </div>
    </div>
  )
}

export default function HomeScreen({
  savedGames = [],
  streak = 0,
  onResume,
  onAbandon,
  onDailyChallenge,
  onTerms,
  onPrivacy,
  onSupport,
  isGuest = false,
  onSignUp,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('guestBannerDismissed') === '1' } catch { return false }
  })
  const dismissGuestBanner = () => {
    try { sessionStorage.setItem('guestBannerDismissed', '1') } catch {}
    setBannerDismissed(true)
  }

  const [getAppDismissed, setGetAppDismissed] = useState(() => {
    try { return sessionStorage.getItem('getAppDismissed') === '1' } catch { return false }
  })
  const dismissGetApp = () => {
    try { sessionStorage.setItem('getAppDismissed', '1') } catch {}
    setGetAppDismissed(true)
  }

  // "Get the App" banner — web only (pointless inside the native app) and
  // dismissible for the session. It stacks with the guest sign-up banner rather
  // than gating it, so a guest can see both.
  const showGetApp = !isNativePlatform() && !getAppDismissed

  const todayStr = getLocalDateStr()

  const [onThisDate, setOnThisDate] = useState(null)
  const [trending, setTrending] = useState(null)

  // Active games only (exclude completed/abandoned for display purposes)
  const activeGames = savedGames.filter(
    g => g.status !== 'completed' && g.status !== 'abandoned'
  )
  const gameCount = activeGames.length

  // Load Premiered Today films
  useEffect(() => {
    let cancelled = false
    getMoviesOnThisDate(todayStr)
      .then((films) => { if (!cancelled) setOnThisDate(films) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Load Trending This Week (TMDB /trending/all/week — combined movies + TV)
  useEffect(() => {
    let cancelled = false
    getYourNextStream()
      .then((films) => { if (!cancelled) setTrending(films) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const collapsedText = gameCount === 1
    ? (() => {
        const g = activeGames[0]
        const start = g.actors?.start?.name
        const goal  = g.actors?.goal?.name
        return start && goal
          ? `Still shooting: ${start} → ${goal}`
          : '1 game in progress'
      })()
    : `${gameCount} games in progress`

  return (
    <div className="home">
      <div className="home__content">

        {/* ── Hero logo lockup (clapperboard + "Action!" wordmark) + tagline ── */}
        <div className="home__hero-lockup">
          <div className="home__hero">
            <img
              src="/clapperboard.png"
              alt="Action!"
              className="home__hero-clapper clapper-shimmer"
              draggable={false}
            />
            <BrandLogo className="home__hero-wordmark" />
          </div>
          <p className="home__hero-tagline">Connect the Stars!</p>
        </div>

        {/* ── Saved Games Dropdown ── */}
        {gameCount > 0 && (
          <div className={`sgd${dropdownOpen ? ' sgd--open' : ''}`}>
            <button
              type="button"
              className="sgd__header"
              onClick={() => setDropdownOpen(o => !o)}
              aria-expanded={dropdownOpen}
            >
              <span className="sgd__header-left">
                <span className="sgd__clapper" aria-hidden="true"><IconClapper size={18} /></span>
                <span className="sgd__summary">{collapsedText}</span>
              </span>
              <span
                className="sgd__chevron"
                style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                aria-hidden="true"
              >
                ▾
              </span>
            </button>

            {dropdownOpen && (
              <div className="sgd__list">
                {activeGames.map(entry => (
                  <SavedGameCard
                    key={entry.id}
                    entry={entry}
                    onResume={onResume}
                    onAbandon={onAbandon}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Get the App banner (web only) — stacks above the guest banner ── */}
        {showGetApp && (
          <div className="getapp-banner">
            <div className="getapp-banner__icon"><IconAppStore size={22} /></div>
            <div className="getapp-banner__copy">
              <div className="getapp-banner__title">Action! on your phone</div>
              <div className="getapp-banner__sub">Play anywhere — download the app</div>
            </div>
            <a className="getapp-banner__cta" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <IconApple size={15} /> Get
            </a>
            <button className="getapp-banner__dismiss" aria-label="Dismiss" onClick={dismissGetApp}>
              <IconX size={14} />
            </button>
          </div>
        )}

        {/* ── Guest banner (guests only) — sign-up / free tickets ── */}
        {isGuest && !bannerDismissed && (
          <div className="guest-banner">
            <div className="guest-banner__icon"><IconTicket size={20} /></div>
            <div className="guest-banner__copy">
              <div className="guest-banner__title">10 Tickets FREE</div>
              <div className="guest-banner__sub">Sign up to keep your streak &amp; stats</div>
            </div>
            <button className="guest-banner__cta" onClick={onSignUp}>
              Claim <IconArrowRight size={14} />
            </button>
            <button className="guest-banner__dismiss" aria-label="Dismiss" onClick={dismissGuestBanner}>
              <IconX size={14} />
            </button>
          </div>
        )}

        {/* ── Daily Challenge label row: label + streak ── */}
        <div className="home__daily-header">
          <span className="home__daily-label">DAILY CHALLENGE</span>
          <span className="home__daily-streak" aria-label={`${streak} day streak`}>
<IconFlame size={13} />{streak}
          </span>
        </div>

        {/* ── Daily Challenge card (two states) ── */}
        <DailyCard onActivate={onDailyChallenge} />

        {/* ── Premiered Today ── */}
        <OnThisDateSection films={onThisDate} />

        {/* ── Trending ── */}
        <TrendingSection films={trending} />

        {/* ── Footer — standardized TMDB attribution + legal links ── */}
        <div className="home__footer">
          <TmdbFooter />
          <div className="home__legal-links">
            <button type="button" className="home__legal-link" onClick={onSupport}>Support</button>
            <span className="home__legal-sep" aria-hidden="true">·</span>
            <button type="button" className="home__legal-link" onClick={onTerms}>Terms</button>
            <span className="home__legal-sep" aria-hidden="true">·</span>
            <button type="button" className="home__legal-link" onClick={onPrivacy}>Privacy</button>
          </div>
        </div>
      </div>
    </div>
  )
}
